// First-party campaign attribution: which ad or link brought a parent here. The marketing pages
// (public/club/site.js, which repeats CAMPAIGN_PATTERN because it loads no modules) and the app's
// join screen read these four parameters from the landing URL and keep them in sessionStorage for
// the tab. Nothing is sent anywhere but our own /api/beta and /api/signup, and no cookie is set.
//
// Only short tokens survive. Anything longer, or with a space, @, slash or other character, is
// dropped rather than rejected, so a mangled link never blocks a sign-up and no free text (an email
// pasted into a link, say) is ever stored.
export const CAMPAIGN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'ref'] as const;
export type CampaignParam = typeof CAMPAIGN_PARAMS[number];
export type Campaign = Partial<Record<CampaignParam, string>>;
export const CAMPAIGN_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;
export const CAMPAIGN_STORAGE_KEY = 'mm_campaign';

/** Keeps the valid campaign values from any input, and drops everything else. */
export function cleanCampaign(input: unknown): Campaign {
  const out: Campaign = {};
  if (!input || typeof input !== 'object' || Array.isArray(input)) return out;
  for (const key of CAMPAIGN_PARAMS) {
    const value = (input as Record<string, unknown>)[key];
    if (typeof value === 'string' && CAMPAIGN_PATTERN.test(value)) out[key] = value;
  }
  return out;
}

/** The values to bind, in CAMPAIGN_PARAMS order, NULL where absent. */
export const campaignColumns = (campaign: Campaign): (string | null)[] => CAMPAIGN_PARAMS.map((key) => campaign[key] ?? null);

/** Valid campaign values from a URL query string, or null when the URL carries none. */
export function campaignFromSearch(search: string): Campaign | null {
  const params = new URLSearchParams(search);
  const found = cleanCampaign(Object.fromEntries(CAMPAIGN_PARAMS.map((key) => [key, params.get(key) ?? undefined])));
  return Object.keys(found).length ? found : null;
}

/**
 * Remembers a campaign from this page's URL (the latest link wins) and returns what this tab has
 * remembered. sessionStorage can be missing or throw (private windows, blocked storage); then the
 * URL's own values are used and nothing is remembered.
 */
export function rememberCampaign(search: string, storage: () => Storage | undefined = () => globalThis.sessionStorage): Campaign {
  const fromUrl = campaignFromSearch(search);
  try {
    const store = storage();
    if (!store) return fromUrl ?? {};
    if (fromUrl) store.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify(fromUrl));
    return cleanCampaign(JSON.parse(store.getItem(CAMPAIGN_STORAGE_KEY) ?? 'null'));
  } catch {
    return fromUrl ?? {};
  }
}
